import { InternalDepartment, type InternalRoleDto } from '@swipe4work/models-orval'
import { useUserPreferences } from '@/shared/composables/UseUserPreferences'

/**
 * Maps an {@link InternalDepartment} to the department value used by {@link MenuSetupDialog} (which
 * matches link categories by name). Only differs from the enum for `CustomerSuccess` — the category
 * is named "Customer Success" (with a space).
 */
export const INTERNAL_DEPARTMENT_TO_MENU_VALUE: Record<InternalDepartment, string> = {
  [InternalDepartment.Development]: 'Development',
  [InternalDepartment.Sales]: 'Sales',
  [InternalDepartment.Marketing]: 'Marketing',
  [InternalDepartment.CustomerSuccess]: 'Customer Success',
  [InternalDepartment.Finance]: 'Finance',
}

/** Priority order (matches the dialog's display order) for picking a default when a user has several roles. */
const DEPARTMENT_PRIORITY: InternalDepartment[] = [
  InternalDepartment.Development,
  InternalDepartment.Sales,
  InternalDepartment.CustomerSuccess,
  InternalDepartment.Marketing,
  InternalDepartment.Finance,
]

/**
 * The menu department to pre-select from the internal employee's roles: the first (by
 * {@link DEPARTMENT_PRIORITY}) department the user actually holds a role in, mapped to its menu value.
 * Returns `null` when the user has no internal roles, so the setup dialog falls back to a manual pick.
 */
export function resolveDefaultDepartment(roles: InternalRoleDto[]): string | null {
  const userDepartments = new Set(roles.map(r => r.department))
  const match = DEPARTMENT_PRIORITY.find(d => userDepartments.has(d))

  return match ? INTERNAL_DEPARTMENT_TO_MENU_VALUE[match] : null
}

export type MenuPreferences = {
  /** Selected category ids — every link item in these categories is part of the composed menu. */
  categoryIds: number[]
  /** Selected subcategory keys (`"<categoryId>:::<subcategory>"`) — only those subcategories show. */
  subcategoryKeys: string[]
  /** Individually selected link-item ids, added on top of the category selection. */
  linkItemIds: number[]
  /** Link-item ids the user explicitly hid — removed from both the sidebar and the dashboard. */
  hiddenLinkItemIds: number[]
}

/** Builds the stable key used to select a single subcategory within a category. */
export function subcategoryKey(categoryId: number, subcategory?: string | null): string {
  return `${categoryId}:::${subcategory?.trim() ?? ''}`
}

/**
 * Shared, persisted preference that composes the user's personal menu. Read by both the sidebar
 * and the dashboard overview, and configurable from either settings popover. When no categories or
 * loose links are selected, the menu falls back to showing every link item (minus hidden ones).
 */
export function useMenuPreferences() {
  return useUserPreferences<MenuPreferences>('s4w:menu-preferences', {
    categoryIds: [],
    subcategoryKeys: [],
    linkItemIds: [],
    hiddenLinkItemIds: [],
  })
}
